import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { EXIT_MODAL_SHOWN_KEY } from "./sangjoLandingData";

const readShown = () => {
  try {
    return window.sessionStorage.getItem(EXIT_MODAL_SHOWN_KEY) === "1";
  } catch {
    return false;
  }
};

const markShown = () => {
  try {
    window.sessionStorage.setItem(EXIT_MODAL_SHOWN_KEY, "1");
  } catch {
    // sessionStorage 차단 환경에서는 화면 단위 1회로 동작한다
  }
};

/**
 * 상조 랜딩에서 브라우저 뒤로가기 시 이탈방지 모달을 세션당 1회 띄운다.
 *
 * 광고로 바로 들어온 사용자는 직전 기록이 외부(검색 결과)라서 뒤로가기가 popstate 없이
 * 문서를 떠난다. 그래서 진입 시 같은 주소를 한 칸 더 쌓고, 그 칸을 빠져나오는 첫 뒤로가기를 잡는다.
 *
 * 감지는 router.beforePopState 가 아니라 window popstate 로 한다. Next(13.4) 는 문서 로드 후
 * 첫 popstate 가 현재 주소와 같으면 beforePopState 를 부르지 않고 무시한다(Safari 재오픈 대응,
 * router.js onPopState). 광고·URL 직접 입력 진입이 정확히 그 경우라 beforePopState 로는 안 잡힌다.
 * beforePopState 는 두 번째 이후 popstate 에서 Next 가 같은 페이지를 다시 그리지 않게 막는 데만 쓴다.
 * 페이지 안 CTA(router.push)는 popstate 가 아니라 막지 않는다.
 */
export const useSangjoLandingLeaveGuard = () => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  // 모달을 띄운 뒤의 뒤로가기는 통과시킨다
  const allowLeave = useRef(false);
  // StrictMode 이중 실행에서 트랩이 두 칸 쌓이지 않게 한다
  const trapped = useRef(false);

  useEffect(() => {
    if (readShown()) return;

    const trapPath = router.asPath;
    if (!trapped.current) {
      window.history.pushState(window.history.state, "", trapPath);
      trapped.current = true;
    }

    // 광고 쿼리(n_query 한글 등)는 인코딩 여부가 섞일 수 있어 디코딩해서 비교한다
    const normalize = (path: string) => {
      const withoutHash = path.split("#")[0];
      try {
        return decodeURIComponent(withoutHash);
      } catch {
        return withoutHash;
      }
    };
    const isTrapPath = () =>
      normalize(`${window.location.pathname}${window.location.search}`) === normalize(trapPath);

    // 트랩 칸을 빠져나온 첫 뒤로가기. 주소가 같아 화면은 그대로 두고 모달만 띄운다
    const onPopState = () => {
      if (allowLeave.current || !isTrapPath()) return;
      allowLeave.current = true;
      markShown();
      setIsOpen(true);
    };
    window.addEventListener("popstate", onPopState);

    // 같은 주소로의 popstate 는 Next 가 다시 그리지 않게 막는다. 다른 주소(나가기)는 통과
    router.beforePopState(({ as }) => as !== trapPath);

    return () => {
      window.removeEventListener("popstate", onPopState);
      router.beforePopState(() => true);
    };
    // 진입 시 1회만 건다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** "괜찮아요, 페이지 나가기" — 트랩 칸은 이미 빠져나왔으니 한 번 더 뒤로 가면 원래 이전 페이지다 */
  const leave = () => {
    setIsOpen(false);
    window.history.back();
  };

  return { isOpen, leave };
};
